import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { F, FF, P } from '../utils/format';
import { Card } from '../components/Card';
import { Heading } from '../components/Heading';
import { Slider } from '../components/Slider';
import { Table } from '../components/Table';
import { Grid } from '../components/Grid';
import { Stack } from '../components/Stack';
import { PiggyBank, TrendingUp } from '../components/icons';
import { useApp } from '../hooks/useApp';
import InsightBanner from '../features/motivation/InsightBanner';
import { investmentInsights } from '../features/motivation/motivationEngine';
import PassiveLadder from '../features/passive/PassiveLadder';

export default function Investments() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const insights = investmentInsights(s, proj, pF, blendedReturn);
  return (
  <Stack>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
      {[
        { l: "FHSA", v: pF.fhsa, cl: c.orange },
        { l: "TFSA", v: pF.tfsa, cl: c.green },
        { l: "RRSP", v: pF.rrsp, cl: c.blue },
        { l: "Non-Reg", v: pF.nr, cl: c.purple },
      ].map((x, i) => (
        <Card key={x.l} c={c} delay={i * 0.04} sx={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{x.l}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: x.cl, marginTop: 5 }}>{F(x.v)}</div>
        </Card>
      ))}
    </div>

    <InsightBanner insights={insights} />

    <Grid>
      <Card c={c} delay={0.08}>
        <Heading ic={PiggyBank} c={c}>Contributions</Heading>
        <Slider l="Age 22–25" v={s.s22} fn={(v) => up("s22", v)} min={0} max={80000} step={5000} f="$" c={c} />
        <Slider l="Age 25–30" v={s.s25} fn={(v) => up("s25", v)} min={0} max={120000} step={5000} f="$" c={c} />
        <Slider l="Age 30–35" v={s.s30} fn={(v) => up("s30", v)} min={0} max={150000} step={5000} f="$" c={c} />
        <Slider l="Age 35–40" v={s.s35} fn={(v) => up("s35", v)} min={0} max={200000} step={5000} f="$" c={c} />
      </Card>
      <Card c={c} delay={0.12}>
        <Heading ic={TrendingUp} c={c}>Parameters</Heading>
        <Slider l="Annualized Return" v={s.ret} fn={(v) => up("ret", v)} min={0.04} max={0.15} step={0.005} f="%" c={c} />
        <Slider l="XEQT Allocation" v={s.xeqt} fn={(v) => up("xeqt", v)} min={0} max={100} su="%" c={c} />
        <div style={{ padding: "12px 16px", borderRadius: 12, background: c.card2, fontSize: 13, marginTop: -8, marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: c.muted }}>Blended Return</span>
          <span style={{ color: c.blue, fontWeight: 700 }}>{P(blendedReturn)}</span>
        </div>
        <div style={{ padding: "12px 16px", borderRadius: 12, background: c.card2, fontSize: 13, marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: c.muted }}>Real Return (after inflation)</span>
          <span style={{ color: realReturn > 0 ? c.green : c.red, fontWeight: 700 }}>{P(realReturn)}</span>
        </div>
        <Slider l="TFSA Limit" v={s.tLim} fn={(v) => up("tLim", v)} min={5000} max={10000} step={500} f="$" c={c} />
        <Slider l="RRSP Alloc. %" v={s.rrspPct != null ? s.rrspPct : 60} fn={(v) => up("rrspPct", v)} min={0} max={100} su="%" c={c} />
        <div style={{ padding: "12px 16px", borderRadius: 12, background: c.card2, fontSize: 13, marginTop: 10, display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: c.muted }}>Monthly DCA</span>
          <span style={{ color: c.blue, fontWeight: 700 }}>{FF(Math.round((proj.find(d => d.sv > 0)?.sv || 0) / 12))}</span>
        </div>
        <div style={{ padding: "12px 16px", borderRadius: 12, background: c.card2, fontSize: 13, marginTop: 8, display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: c.muted }}>Lifetime invested</span>
          <span style={{ color: c.green, fontWeight: 700 }}>{F(proj.reduce((a, d) => a + d.sv, 0))}</span>
        </div>
      </Card>
    </Grid>
    <Card c={c} delay={0.15}>
      <Heading ic={TrendingUp} c={c}>Account Growth</Heading>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={proj}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
          <XAxis dataKey="a" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
          <YAxis tickFormatter={F} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
          <Tooltip formatter={(v) => FF(v)} {...tt} />
          <Area type="monotone" dataKey="fhsa" stackId="1" stroke={c.orange} fill={c.orange} fillOpacity={0.2} name="FHSA" />
          <Area type="monotone" dataKey="tfsa" stackId="1" stroke={c.green} fill={c.green} fillOpacity={0.2} name="TFSA" />
          <Area type="monotone" dataKey="rrsp" stackId="1" stroke={c.blue} fill={c.blue} fillOpacity={0.2} name="RRSP" />
          <Area type="monotone" dataKey="nr" stackId="1" stroke={c.purple} fill={c.purple} fillOpacity={0.12} name="Non-Reg" />
        </AreaChart>
      </ResponsiveContainer>
    </Card>

    <PassiveLadder />

    <Card c={c} delay={0.2}>
      <Table h={["Age", "FHSA", "TFSA", "RRSP", "Non-Reg", "Total"]} r={proj.map((d) => [d.a, FF(d.fhsa), FF(d.tfsa), FF(d.rrsp), FF(d.nr), FF(d.liq)])} c={c} />
    </Card>
  </Stack>
  );
}
